import React, { useEffect, useState } from "react"
import { Link, useParams } from 'react-router-dom'
import Slider from "react-slick"
import { addToCart, buyListing, getListingByID, getListingsByUserId, removeFromCart } from "../../firebase/listings"
import DialogConfirmation from "../../components/dialogs/DialogConfirmation"
import DialogLoading from "../../components/dialogs/DialogLoading"
import { getUserData } from "../../firebase/users"
import { getCategoryById } from "../../firebase/categories"
import { toast } from "react-toastify"
import { useAtom } from "jotai"
import { cartListAtom, recipientIdAtom, showIndividualMessageBoxAtom, userAtom } from "../../store"
import { brands } from "../../utils/data"
import Breadcrumb from "../../components/Breadcrumb"
import DialogOffer from "../../components/dialogs/DialogOffer"
import { createCart } from "../../firebase/carts"
import { useAuth } from "../../context/authContext"
import { getShopifyProductByID, isUnavailable, resolveSellerIdentity } from "../../firebase/shopify"
import { getBlockedSellerIds } from "../../firebase/blocks"
import BlockSellerButton from "../../components/commons/BlockSellerButton"

const MarketplaceItemDetails = () => {
  const [openDelivery, setOpenDelivery] = useState(false)
  const [openPayments, setOpenPayments] = useState(false)
  const [cartList, setCartList] = useAtom(cartListAtom)
  const { cardId } = useParams()
  const [card, setCard] = useState(null)
  const [loading, setLoading] = useState(true)
  const [seller, setSeller] = useState(null)
  // The account behind the listing. For a Shopify item the product's seller
  // is the STORE, so this is its current owner, resolved on load rather than
  // read off the product document.
  const [sellerUid, setSellerUid] = useState('')
  const [categoryName, setCategoryName] = useState(null)
  const [userData, setUserData] = useAtom(userAtom)
  const [activeImage, setActiveImage] = useState(null)
  const [showOfferModal, setShowOfferModal] = useState(false)
  const [recipientId, setRecipientId] = useAtom(recipientIdAtom)
  const [showSuccessCartModal, setShowSuccessCartModal] = useState(false)
  const [showIndividualMessageBox, setShowIndividualMessageBox] = useAtom(showIndividualMessageBoxAtom)
  const [activeAddToCart, setActiveAddToCart] = useState(true)
  const { getUpdatedUserData } = useAuth()
  // Whether the buyer has blocked the account behind this listing. The page
  // still opens on a direct link -- it is reached from purchase history and
  // open offers -- but a blocked seller's item can't be bought from it.
  // Blocking and unblocking happen on the seller's profile, not here.
  const [sellerBlocked, setSellerBlocked] = useState(false)

  const verticalThumbSliderSettings = {
    dots: true,
    infinite: false,
    vertical: true,
    slidesToShow: 4,
    slidesToScroll: 1,
    verticalSwiping: true
  }

  const thumbSliderSettings = {
    dots: true,
    infinite: false,
    slidesToShow: 4,
    slidesToScroll: 1,
  }

  const toCapitalize = (str) => {
    if (str && str[0]) return str[0].toUpperCase() + str.slice(1, str.length).toLowerCase()
    return str
  }

  useEffect(() => {
    const loadItem = async () => {
      const localItem = await getListingByID(cardId)

      if (localItem) {
        setCard(localItem)
        setActiveImage(localItem.pictures[0])
        setLoading(false)
        if (localItem.quantity === 0) setActiveAddToCart(false)
        return
      }

      const shopifyItem = await getShopifyProductByID(cardId)
      if (shopifyItem) {
        setCard(shopifyItem)
        setActiveImage(shopifyItem.pictures[0])
        setLoading(false)
      }
    }

    loadItem()
    scrollTo({ top: 0 })
  }, [cardId])

  useEffect(() => {
    if (!card) return

    // A Shopify product's seller_id is a store role id, not a user id, so
    // looking it up directly found no account and the page fell back to
    // showing a generic "Shopify" as the seller.
    resolveSellerIdentity(card).then(({ uid }) => {
      setSellerUid(uid || '')

      if (!uid) {
        setSeller(null)
        return
      }

      getUserData(uid)
        .then((item) => setSeller(item))
        .catch(() => setSeller(null))
    })

    if (card.seller) {
      getCategoryById(card.category).then((item) => {
        // A Shopify listing not yet assigned a real category holds the
        // literal string 'shopify' here, which resolves to no category doc.
        setCategoryName(item?.name || (card.source === 'shopify' ? 'Shopify' : null))
      })
    } else if (card.source === 'shopify') {
      setCategoryName('Shopify')
    }
  }, [card])

  // Keyed to the resolved account, not card.seller: for a Shopify item that
  // is the store's role id, and what the buyer blocked is the owner.
  useEffect(() => {
    if (!userData?.id || !sellerUid) {
      setSellerBlocked(false)
      return
    }

    getBlockedSellerIds(userData.id).then((ids) => setSellerBlocked(ids.has(sellerUid)))
  }, [userData?.id, sellerUid])

  useEffect(() => {
    if (!card) return
    setActiveAddToCart(!listingIsInCart())
  }, [cartList, card, seller])

  const listingIsInCart = () => {
    if (!card) return false
    return cartList.findIndex(item => item.id === card.id) >= 0
  }

  const formatDate = (date) => {
    const options = { hour: 'numeric', minute: 'numeric', hour12: true }
    const timeString = date.toLocaleTimeString('en-US', options)
    const dateString = `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`

    return `${timeString} ${dateString}`
  }

  const setMessageToSeller = () => {
    if (!sellerUid) return
    setShowIndividualMessageBox(true)
    // The resolved account, not card.seller -- for a Shopify item that is a
    // store role id, which has no inbox.
    setRecipientId(sellerUid)
  }

  const handleCreateCart = () => {
    setCartList((prev) => {
      if (prev.some((item) => item.id === card.id)) {
        return prev
      }

      return [
        ...prev,
        {
          ...card,
          count: 1,
          managed_by_shopify: card?.source === 'shopify',
          inventory_source: card?.source === 'shopify' ? 'shopify' : undefined,
          merchant_id: card?.merchant_id || card?.seller_id || card?.seller || (card?.source === 'shopify' ? 'shopify' : undefined),
          seller_id: card?.seller_id || card?.seller || '',
          seller: card?.seller || card?.seller_id || '',
          shopify_store_domain: card?.shopify_store_domain || '',
        }
      ]
    })
  }

  const handleAddToCart = async () => {
    if (!card || isUnavailable(card) || sellerBlocked) return
    if (card?.source === 'shopify' && !card?.seller_id && !card?.seller) {
      return
    }

    if (card?.source !== 'shopify' && !seller) return

    if (listingIsInCart()) return

    if (card?.source === 'shopify') {
      handleCreateCart()
    } else {
      handleCreateCart(userData.id, card)
    }
    setShowSuccessCartModal(true)
  }

  const handleMakeOffer = () => {
    if (!card || isUnavailable(card) || sellerBlocked) return

    const hasSellerReference = Boolean(card?.seller || card?.seller_id || card?.merchant_id || card?.seller_email)
    if (!hasSellerReference && card?.source !== 'shopify') return

    if (card?.source === 'shopify') {
      setShowOfferModal(true)
      return
    }

    if (!seller) return

    setShowOfferModal(true)
  }

  return (
    <div>
      {loading &&
        <DialogLoading
          onClose={() => { }}
        />}
      <DialogConfirmation
        message="Your listing has been successfully added to cart!"
        open={showSuccessCartModal}
        onClose={() => setShowSuccessCartModal(false)}
        buttonText="Done"
        title="Listing Carted"
        iconSrc="/assets/icons/icon-check-circle.svg"
        type="confirm"
      />

      {
        card &&
        <DialogOffer
          open={showOfferModal}
          card={card}
          onClose={() => { setShowOfferModal(false) }}
        />
      }

      <Breadcrumb link="/marketplace/categories" />

      {
        !loading && card && (seller || card.source === 'shopify') ?
          <div className="flex flex-col lg:flex-row items-start gap-6 lg:gap-12">
            <div className="w-full grid grid-cols-1 lg:grid-cols-5 gap-5">
              {/* Thumbnail Slider */}
              {/* <Slider {...verticalThumbSliderSettings} arrows={false} className="col-span-1 max-lg:!hidden"> */}
              <div className="flex flex-col gap-y-4 max-lg:!hidden">
                {card.pictures.slice(0, 5).map((image) => (
                  <div key={`thumb-${image}`} className="px-[2px] h-[20%] lg:px-1">
                    <div
                      className={`relative cursor-pointe h-full w-full ${activeImage === image
                        ? "!opacity-100 !border-primary"
                        : "opacity-40"
                        }`}
                      onClick={() => setActiveImage(image)}
                    >
                      <img
                        src={image}
                        className="absolute top-0 left-0 w-full object-cover h-full bg-white border-primary border-[1px]"
                        alt={`Thumbnail ${image}`}
                      />
                    </div>
                  </div>
                ))}
              </div>
              {/* </Slider> */}

              {/* Main Displayed Image */}
              <div className="product-ask-section w-full pt-[100%] lg:col-span-4">
                <img
                  src={activeImage}
                  className="absolute top-0 w-full h-full object-cover bg-white border-primary border-[1px]"
                  alt="Main Displayed"
                />
              </div>

              {/* <Slider {...thumbSliderSettings} arrows={false} className='lg:!hidden space-10'> */}
              <div className="grid grid-cols-5  sm:gap-x-4 gap-x-1 !w-full lg:!hidden">
                {card.pictures.map((image) => (
                  <div key={`thumb-${image}`} className="px-[2px] lg:px-1">
                    <div
                      className={`relative cursor-pointe w-full pt-[100%] ${activeImage === image
                        ? "!opacity-100 !border-primary"
                        : "opacity-40"
                        }`}
                      onClick={() => setActiveImage(image)}
                    >
                      <img
                        src={image}
                        className="absolute top-0 left-0 w-full object-cover h-full bg-white border-primary border-[1px]"
                        alt={`Thumbnail ${image}`}
                      />
                    </div>
                  </div>
                ))}
              </div>
              {/* </Slider> */}
            </div>

            <div className="w-full px-4 py-5 border-[#434343] border-[1px]">
              <h1 className="uppercase text-white font-bold leading-[1.2] text-[20px] lg:text-[20px]">
                {card.title}
              </h1>

              <hr className="my-4 border-gray"></hr>

              <div className="w-full md:flex items-center justify-between mt-2 lg:mt-0">
                <div>
                  <div className="flex items-center text-primary text-[20px] lg:text-[20px] font-aeonik font-bold">
                    ${card.price.toFixed(2)}
                    {/* <span className="text-white font-thin text-sm ml-3">Listing Price</span> */}
                  </div>

                  {
                    card.original_price && userData.id === seller.id &&
                    <div className="flex items-center text-[#454545] text-[20px] lg:text-[20px] font-aeonik font-bold">
                      ${card.original_price.toFixed(2)}
                      {/* <span className="text-white font-thin text-sm ml-3">Seller Cost</span> */}
                    </div>
                  }
                </div>

                {/* <span className="text-primary text-[20px] lg:text-[20px] font-aeonik font-bold">
                  ${card.price.toFixed(2)}
                </span> */}

                <hr className="my-4 border-gray md:hidden"></hr>

                {/* A Shopify listing the store owner has switched off. The
                    page still renders -- it is reached from purchase history
                    and open offers -- but nothing on it can start a purchase. */}
                {isUnavailable(card) && (
                  <div className="border border-[#434343] px-4 py-3">
                    <span className="uppercase text-sm text-[#717171]">
                      This listing is currently unavailable
                    </span>
                  </div>
                )}

                {/* The buyer chose not to see this seller, so there is
                    nothing to buy here either. The seller panel below carries
                    the unblock. */}
                {!isUnavailable(card) && sellerBlocked && (
                  <div className="border border-[#434343] px-4 py-3">
                    <span className="uppercase text-sm text-[#717171]">
                      You have blocked this seller
                    </span>
                  </div>
                )}

                {
                  !isUnavailable(card) && !sellerBlocked && (card.source === 'shopify' || (userData && seller && userData.id !== seller.id)) && (
                    <div className="flex">
                      {card.source !== 'shopify' && (
                        <button
                          className={`mr-2 uppercase text-sm bg-primary px-8 py-3 text-white transition-all  ${card.quantity === 0 ? 'opacity-50' : ' hover:bg-white hover:text-black'}`}
                          disabled={card.quantity === 0}
                          onClick={handleMakeOffer}
                        >
                          Make Offer
                        </button>
                      )}

                      {card.source === 'shopify' && (
                        <button
                          className={`mr-2 uppercase text-sm bg-primary px-8 py-3 text-white transition-all ${card.quantity === 0 ? 'opacity-50' : ' hover:bg-white hover:text-black'}`}
                          disabled={card.quantity === 0}
                          onClick={handleMakeOffer}
                        >
                          Make Offer
                        </button>
                      )}

                      <button
                        className={`uppercase text-sm bg-white px-8 py-3 text-black transition-all ${!activeAddToCart ? 'opacity-50' : ' hover:bg-primary hover:text-white'}`}
                        onClick={handleAddToCart}
                        disabled={!activeAddToCart}
                      >
                        {listingIsInCart() ? 'Added to Cart' : 'Add to Cart'}
                      </button>
                    </div>
                  )
                }
              </div>

              <hr className="my-4 border-gray"></hr>

              <span className="uppercase text-primary text-[12px]">Description:</span>

              <p className="text-white text-sm font-thin mt-4 lg:mt-0">
                {card.description}

                {
                  seller && sellerUid && sellerUid !== userData.id ? (

                    <div className="w-full border border-[#FFFFFF]/20 p-3 lg:p-5 mt-8">
                      <div className="w-full flex items-center justify-between pb-3 border-b border-b-[#fff]/20">
                        <span className="font-aero uppercase text-base lg:text-xl text-white">
                          Seller
                        </span>
                        <div className="flex items-center space-x-2">
                          <div className="flex items-center">
                            {Array.from({ length: 5 }).map((_, index) => (
                              <img
                                key={index}
                                src="/assets/icons/icon-star-fill.svg"
                                className={`w-[14px] lg:w-[24px] ${index < seller.ratings ? "" : "opacity-30"}`}
                                alt="star"
                              />
                            ))}
                          </div>
                          <span className="text-xs lg:text-sm text-white block">
                            {Number(seller.ratings || 0).toFixed(1)}
                          </span>
                        </div>
                      </div>

                      <div className="pt-5 flex flex-wrap gap-y-5 items-center justify-between">

                        <div className="flex items-center space-x-2 lg:space-x-4">
                          <div className="w-12 h-12 flex justify-center items-center">
                            <img src="/assets/avatars/default.png" className="w-full h-full" alt="seller" />
                          </div>

                          <div className="flex flex-col items-start">
                            <span className="text-sm lg:text-base text-white">
                              {seller.displayName}
                            </span>

                            <div className="flex items-center gap-3">
                              <Link className="text-[10px] lg:text-xs flex items-center text-primary underline" to={`/user-profile/${sellerUid}`}>
                                View Profile
                              </Link>

                              <BlockSellerButton
                                variant="link"
                                seller={{ id: sellerUid, displayName: seller.displayName, picture: seller.picture }}
                                onChange={setSellerBlocked}
                              />
                            </div>
                          </div>
                        </div>
                        <button
                          className="flex border-[1px] border-primary text-sm sm:px-8 px-4 py-3 bg-[#24343C] text-white transition-all"
                          onClick={setMessageToSeller}
                        >
                          <img src="/assets/icons/icon-message.svg" alt='message' className="mr-1" />

                          Send Message
                        </button>
                      </div>
                    </div>
                    ) :
                    <hr className="my-4 border-gray"></hr>
                }

                <div className={`w-full space-y-4 ${sellerUid !== userData.id ? 'mt-8' : ''}`}>
                  <div className="w-full flex">
                    <span className="uppercase text-xs font-bold text-primary w-full max-w-[140px] lg:max-w-[210px] block">
                      Release Year:
                    </span>
                    <span>{card.released_year}</span>
                  </div>
                  <div className="w-full flex">
                    <span className="uppercase text-xs font-bold text-primary w-full max-w-[140px] lg:max-w-[210px] block">
                      Condition:
                    </span>
                    <span>{toCapitalize(card.condition)}</span>
                  </div>
                  <div className="w-full flex">
                    <span className="uppercase text-xs font-bold text-primary w-full max-w-[140px] lg:max-w-[210px] block">
                      Sub Category:
                    </span>
                    <span>{toCapitalize(card.sub_category)}</span>
                  </div>
                  <div className="w-full flex">
                    <span className="uppercase text-xs font-bold text-primary w-full max-w-[140px] lg:max-w-[210px] block">
                      Box Type:
                    </span>
                    <span>{toCapitalize(card.rarity)}</span>
                  </div>
                  <div className="w-full flex">
                    <span className="uppercase text-xs font-bold text-primary w-full max-w-[140px] lg:max-w-[210px] block">
                      Date Posted:
                    </span>
                    <span>{formatDate(new Date(card.created_at.seconds * 1000))}</span>
                  </div>
                  <div className="w-full flex">
                    <span className="uppercase text-xs font-bold text-primary w-full max-w-[140px] lg:max-w-[210px] block">
                      In Stock:
                    </span>
                    <span>{card.quantity}</span>
                  </div>
                </div>
              </p>

              <hr className="my-4 border-gray"></hr>
              <div className="mt-6 ">
                <div
                  className="w-full flex justify-between items-center cursor-pointer pb-6 mb-6 border-b border-b-white/20"
                  onClick={() => setOpenDelivery(!openDelivery)}
                >
                  <span className="text-white font-aero uppercase text-2xl">
                    Delivery & returns
                  </span>
                  <img
                    src="/assets/icons/icon-arrow-drop-down.svg"
                    className={`${openDelivery ? "rotate-180" : ""}`}
                  />
                </div>

                {openDelivery && (
                  <div className="text-white w-full">

                    <div className="w-full space-y-4  mt-6">
                      <div className="w-full flex">
                        <img
                          alt="circle"
                          src="/assets/icons/icon-check-circle.svg"
                          className="w-[25px] mr-3"
                        />
                        <span className="text-xs lg:text-base">
                          Prices include shipping OR add a separate price box for shipping prices
                        </span>
                      </div>
                      <div className="w-full flex">
                        <img
                          alt="circle"
                          src="/assets/icons/icon-check-circle.svg"
                          className="w-[25px] mr-3"
                        />
                        <span className="text-xs lg:text-base">
                          Please allow for 2-4 business days for processing.
                        </span>
                      </div>
                      <div className="w-full flex">
                        <img
                          alt="circle"
                          src="/assets/icons/icon-check-circle.svg"
                          className="w-[25px] mr-3"
                        />
                        <span className="text-xs lg:text-base">
                          For trading cards and sticker items, we have a no-return
                          policy in place.
                        </span>
                      </div>

                      <span className="text-xs lg:text-base block text-primary">
                        1-3 business days
                      </span>

                      <div className="flex items-center gap-4 pb-8">
                        <div className="border-style-decoration bg-white/5 !border-none backdrop-blur-sm p-4 w-fit">
                          <img
                            src="/assets/icons/icon-package.svg"
                            className="max-w-[16px] lg:max-w-[unset]"
                          />
                        </div>
                        <div className="flex flex-col">
                          <span className="text-xs leading-[1.4] lg:text-base text-white/60 block mt-1">
                            The seller will typically provide you with the tracking
                            <br /> number within 4-5 business days
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-6">
                <div
                  className="w-full flex justify-between items-center cursor-pointer pb-6 mb-4 border-b border-b-white/20"
                  onClick={() => setOpenPayments(!openPayments)}
                >
                  <span className="text-white font-aero uppercase text-2xl">
                    Payments
                  </span>
                  <img
                    src="/assets/icons/icon-arrow-drop-down.svg"
                    className={`${openPayments ? "rotate-180" : ""}`}
                  />
                </div>

                {openPayments && (
                  <div className="text-white w-full">
                    <p className="text-xs lg:text-base opacity-60">
                      We accept all major credit and debit cards, including Visa,
                      Mastercard, and American<br /> Express.
                    </p>

                    <img
                      src="/assets/images/payment_lists.svg"
                      className="mt-5 max-w-[230px] lg:max-w-[unset]"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
          : ""
      }
    </div >
  )
}

export default MarketplaceItemDetails
